import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  Button,
  Collapse,
  FormGroup,
  Checkbox,
  Grid,
  TextField
} from "@mui/material";
import { useGenerateClipping } from "../../../hooks/useClipping/usePostGenerateClipping";
import { postSectionClippingURL, postDocumentClippingURL } from "../../../utils/ConstantsURL";

export const ClippingSettingsModal = ({
  open,
  onClose,
  token,
  idDocument,
  nameDocument,
  sections,
  fecha,
  addToDownloadQueue,
  addToHiperlyncQueue,
}) => {
  const { generateClipping } = useGenerateClipping(token, addToDownloadQueue, addToHiperlyncQueue);
  const [editNameEnabled, setEditNameEnabled] = useState(false);
  const [customName, setCustomName] = useState("");
  const [writeDate, setWriteDate] = useState(null);
  const [underlineEnabled, setUnderlineEnabled] = useState(false);
  const [underline, setUnderline] = useState("");
  const [excludeWordsEnabled, setExcludeWordsEnabled] = useState(false);
  const [excludeWords, setExcludeWords] = useState("");

  const [config, setConfig] = useState({
    cover: false,
    index: false,
    dividers: false,
    sectionsEnabled: false,
    selectedSections: []
  });

  const handleChange = (key) => {
    setConfig((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSectionChange = (section) => {
    setConfig((prev) => {
      const selectedSections = prev.selectedSections.includes(section.ID_SECCION)
        ? prev.selectedSections.filter((s) => s !== section.ID_SECCION)
        : [...prev.selectedSections, section.ID_SECCION];
      return { ...prev, selectedSections };
    });
  };

  const handleGenerate = (includeHipervinculo = false) => {
    let generatedName = nameDocument;

    if (editNameEnabled && customName.trim() !== "") {
      generatedName = customName.trim();
    } else if (config.sectionsEnabled) {
      const selectedSectionNames = sections
        .filter(section => config.selectedSections.includes(section.ID_SECCION))
        .map(section => section.NOMBRE_SECCION);

      if (config.selectedSections.length === 1) {
        generatedName = selectedSectionNames[0];
      } else if (config.selectedSections.length > 1) {
        generatedName = `${nameDocument} (secciones: ${config.selectedSections.length})`;
      }
    }

    const parsedUnderline = underlineEnabled && underline
      ? underline.split(",").map((u) => u.trim()).filter(Boolean)
      : undefined;

    const parsedExcludeWords = excludeWordsEnabled && excludeWords
      ? excludeWords.split(",").map((e) => e.trim()).filter(Boolean)
      : undefined;

    const endpoint = config.sectionsEnabled
      ? postSectionClippingURL
      : postDocumentClippingURL;

    generateClipping(
      idDocument,
      fecha,
      generatedName,
      config.sectionsEnabled,
      config.selectedSections,
      onClose,
      {
        cover: config.cover,
        index: config.index,
        dividers: config.dividers,
      },
      includeHipervinculo,
      endpoint,
      {
        cover: writeDate === "cover",
        index: writeDate === "index",
      },
      parsedUnderline,
      parsedExcludeWords,
    );
  };

  React.useEffect(() => {
    if (writeDate === "cover" && !config.cover) {
      setWriteDate(null);
    }

    if (writeDate === "index" && !config.index) {
      setWriteDate(null);
    }
  }, [config.cover, config.index, writeDate]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          maxHeight: "80vh",
          overflowY: "auto",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 2
        }}
      >
        <Typography variant="h6" gutterBottom>
          Configuración de Clipping
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={<Switch checked={config.cover} onChange={() => handleChange("cover")} />}
            label="Portada"
          />
          <FormControlLabel
            control={<Switch checked={config.index} onChange={() => handleChange("index")} />}
            label="Índice"
          />
          {(config.cover || config.index) && (
            <Box sx={{ mt: 2, ml: 1 }}>
              <Typography variant="subtitle2">
                Mostrar fecha
              </Typography>

              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={writeDate === "cover"}
                      disabled={!config.cover}
                      onChange={() =>
                        setWriteDate(writeDate === "cover" ? null : "cover")
                      }
                    />
                  }
                  label="En portada"
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={writeDate === "index"}
                      disabled={!config.index}
                      onChange={() =>
                        setWriteDate(writeDate === "index" ? null : "index")
                      }
                    />
                  }
                  label="En índice"
                />
              </FormGroup>
            </Box>
          )}
          <FormControlLabel
            control={<Switch checked={config.dividers} onChange={() => handleChange("dividers")} />}
            label="Separadores"
          />
          <FormControlLabel
            control={<Switch checked={config.sectionsEnabled} onChange={() => handleChange("sectionsEnabled")} />}
            label="Secciones"
          />
          <Collapse in={config.sectionsEnabled}>
            <Box sx={{ ml: 2, maxHeight: "200px", overflowY: "auto" }}>
              <Grid container spacing={1} >
                {sections.map((section) => (
                  <Grid item xs={12} key={section.ID_SECCION}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={config.selectedSections.includes(section.ID_SECCION)}
                          onChange={() => handleSectionChange(section)}
                        />
                      }
                      label={section.NOMBRE_SECCION}
                      
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Collapse>

          <FormControlLabel
            control={
              <Switch
                checked={editNameEnabled}
                onChange={() => setEditNameEnabled((prev) => !prev)}
              />
            }
            label="Editar nombre del documento"
          />
          <Collapse in={editNameEnabled}>
            <TextField
              fullWidth
              size="small"
              label="Nombre del archivo"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              sx={{ mt: 1 }}
            />
          </Collapse>

          <FormControlLabel
            control={
              <Switch
                checked={underlineEnabled}
                onChange={() => setUnderlineEnabled((prev) => !prev)}
              />
            }
            label="Subrayar palabras"
          />
          <Collapse in={underlineEnabled}>
            <TextField
              fullWidth
              size="small"
              label="Palabras a subrayar (separadas por coma)"
              value={underline}
              onChange={(e) => setUnderline(e.target.value)}
              sx={{ mt: 1 }}
            />
          </Collapse>

          <FormControlLabel
            control={
              <Switch
                checked={excludeWordsEnabled}
                onChange={() => setExcludeWordsEnabled((prev) => !prev)}
              />
            }
            label="Excluir notas que contengan palabras"
          />
          <Collapse in={excludeWordsEnabled}>
            <TextField
              fullWidth
              size="small"
              label="Palabras prohibidas (separadas por coma)"
              value={excludeWords}
              onChange={(e) => setExcludeWords(e.target.value)}
              sx={{ mt: 1, mb: 1 }}
            />
          </Collapse>
        </FormGroup>

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
          <Button
            variant="contained"
            onClick={() => handleGenerate(true)}
            sx={{ px: 2 }}
            disabled={config.sectionsEnabled && config.selectedSections.length === 0}
          >
            Hipervínculo
          </Button>
          <Button
            variant="contained"
            onClick={() => handleGenerate(false)}
            sx={{ px: 3 }}
            disabled={config.sectionsEnabled && config.selectedSections.length === 0}
          >
            Clipping
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
